import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/i18n';
import '@/App.css';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import MessagingPanel, { MessageButton } from '@/components/MessagingPanel';
import PageTracker from '@/components/PageTracker';
import AutoLanguageDetector from '@/components/AutoLanguageDetector';

// Google Analytics initialization
const initGA = () => {
  if (typeof window !== 'undefined' && !window.gtag) {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-CGNJEMSLSN';
    document.head.appendChild(script);
    
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', 'G-CGNJEMSLSN');
  }
};
initGA();

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    // Force scroll to top with a small delay to ensure DOM is ready
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 0);
  }, [pathname]);
  
  return null;
};

// Pages
import HomePage from '@/pages/HomePage';
import { LoginPage, RegisterPage } from '@/pages/AuthPages';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import AuthCallback from '@/pages/AuthCallback';
import ExplorePage from '@/pages/ExplorePage';
import ExperienceDetailPage from '@/pages/ExperienceDetailPage';
import { BookingPage, PaymentSuccessPage, PaymentCancelPage } from '@/pages/BookingPages';
import DashboardPage from '@/pages/DashboardPage';
import ExplorerProfilePage from '@/pages/ExplorerProfilePage';
import MapPage from '@/pages/MapPage';
import AboutPage from '@/pages/AboutPage';
import FounderPage from '@/pages/FounderPage';
import GuideCharterPage from '@/pages/GuideCharterPage';
import TravelerCharterPage from '@/pages/TravelerCharterPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import CareersPage from '@/pages/CareersPage';
import PressPage from '@/pages/PressPage';
import HelpPage from '@/pages/HelpPage';
import AdminPage from '@/pages/AdminPage';
import ContactPage from '@/pages/ContactPage';
import NotFoundPage from '@/pages/NotFoundPage';
import GuideFaqPage from '@/pages/GuideFaqPage';
import HowItWorksPage from '@/pages/HowItWorksPage';
import DestinationPage from '@/pages/DestinationPage';
import WelcomePage from '@/pages/WelcomePage';
import SupportUsPage from '@/pages/SupportUsPage';
import PartnersPage from '@/pages/PartnersPage';
import { DonationSuccessPage } from '@/components/DonationModal';
import { SubscriptionSuccessPage, SubscriptionCancelPage } from '@/components/SubscriptionCard';
import MembershipPage from '@/pages/MembershipPage';
import CookieConsent from '@/components/CookieConsent';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with OAuth callback detection
const AppRouter = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [messagingOpen, setMessagingOpen] = useState(false);
  
  // Check URL fragment (not query params) for session_id - BEFORE rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <PageTracker />
      <AutoLanguageDetector />
      <Navbar />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/experience/:id" element={<ExperienceDetailPage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/founder" element={<FounderPage />} />
        <Route path="/guide-charter" element={<GuideCharterPage />} />
        <Route path="/traveler-charter" element={<TravelerCharterPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/press" element={<PressPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/membership" element={<MembershipPage />} />
        <Route path="/subscription/success" element={
          <ProtectedRoute>
            <SubscriptionSuccessPage />
          </ProtectedRoute>
        } />
        <Route path="/subscription/cancel" element={
          <ProtectedRoute>
            <SubscriptionCancelPage />
          </ProtectedRoute>
        } />
        
        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/booking/:id" element={
          <ProtectedRoute>
            <BookingPage />
          </ProtectedRoute>
        } />
        <Route path="/payment/success" element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        } />
        <Route path="/payment/cancel" element={
          <ProtectedRoute>
            <PaymentCancelPage />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } />
        
        {/* Welcome Page after registration */}
        <Route path="/welcome" element={
          <ProtectedRoute>
            <WelcomePage />
          </ProtectedRoute>
        } />

        {/* Explorer Profile (Public) */}
        <Route path="/explorer/:id" element={<ExplorerProfilePage />} />
        
        {/* Contact Page */}
        <Route path="/contact" element={<ContactPage />} />
        
        {/* Guide FAQ / Info Page */}
        <Route path="/guide-faq" element={<GuideFaqPage />} />
        <Route path="/devenir-guide" element={<GuideFaqPage />} />
        
        {/* How It Works Page */}
        <Route path="/comment-ca-marche" element={<HowItWorksPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        
        {/* Destination Pages (SEO) */}
        <Route path="/destination/:slug" element={<DestinationPage />} />
        
        {/* Donation Pages */}
        <Route path="/donation/success" element={<DonationSuccessPage />} />
        
        {/* Support & Partners Pages */}
        <Route path="/nous-soutenir" element={<SupportUsPage />} />
        <Route path="/support" element={<SupportUsPage />} />
        <Route path="/partenaires" element={<PartnersPage />} />
        <Route path="/partners" element={<PartnersPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
      
      {/* Floating Message Button (only for authenticated users) */}
      {isAuthenticated && (
        <div className="fixed bottom-6 left-6 z-[100]">
          <MessageButton onClick={() => setMessagingOpen(true)} />
        </div>
      )}
      
      {/* Messaging Panel */}
      <MessagingPanel isOpen={messagingOpen} onClose={() => setMessagingOpen(false)} />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AppRouter />
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: 'white',
                border: '1px solid hsl(220, 13%, 91%)',
                borderRadius: '12px',
              },
            }}
          />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
